import {Entity} from "@commons/Models";
import {Reward} from "@lib/Reward/RewardModels";
import {CmsBanner, CmsSection} from "@lib/Cms/CmsModels";

export enum EVENT_PERIODICITY_ENUM {
    ALWAYS = "ALWAYS",
    HOURLY = "HOURLY",
    DAILY = "DAILY",
    WEEKLY = "WEEKLY",
    MONTHLY = "MONTHLY",
    YEARLY = "YEARLY"
}

export enum EVENT_STATUS_ENUM {
    UPCOMING = "UPCOMING",
    CLOSED = "CLOSED",
    CLOSING = "CLOSING",
    OPEN = "OPEN",
    COMPLETED = "COMPLETED"
}

export enum EVENT_CONFIGURATION_STATUS_ENUM {
    UPCOMING = "UPCOMING",
    CLOSED = "CLOSED",
    CLOSING = "CLOSING",
    OPEN = "OPEN"
}

export enum EVENT_USER_STATUS_ENUM {
    TO_DO = "TO_DO",
    IN_PROGRESS = "IN_PROGRESS",
    COMPLETED = "COMPLETED"
}

export enum CLUSTER_EVENT_ACCESS_ENUM {
    PLAY = "PLAY",
    VIEW = "VIEW"
}

export enum EventCategoryEnum {
    CORE = "CORE",
    EXTRA = "EXTRA"
}

export enum EventTypeEnum {
    PDA = "PDA",
    KEYLIST = "KEYLIST",
    SDG = "SDG",
    WELCOME = "WELCOME",
    CHANGE_CLUSTER = "CHANGE_CLUSTER",
    FIDELITY = "FIDELITY",
    BIRTHDAY = "BIRTHDAY",
    CONTEST = "CONTEST",
    LOGIN = "LOGIN",
    SOCIAL_LOGIN = "SOCIAL_LOGIN",
    WALLET = "WALLET",
    QUESTIONNAIRE = "SURVEY",
    PATTERN = "PATTERN",
}

export interface EventGroupFilters {
    retrieveEventResponses?: boolean;
}

export enum UN_PLAYABLE_REASON_CODE {
    NOT_PLAYABLE_INSUFFICIENT_BALANCE = "NOT_PLAYABLE_INSUFFICIENT_BALANCE",
    NOT_PLAYABLE_LIMIT_EXCEEDED = "NOT_PLAYABLE_LIMIT_EXCEEDED",
    NOT_PLAYABLE_INVALID_CLUSTER = "NOT_PLAYABLE_INVALID_CLUSTER",
    NOT_PLAYABLE_DATE_OUT_OF_RANGE = "NOT_PLAYABLE_DATE_OUT_OF_RANGE",
    NOT_PLAYABLE_TIME_OUT_OF_RANGE = "NOT_PLAYABLE_TIME_OUT_OF_RANGE",
    NOT_PLAYABLE_EVENT_UNAVAILABLE = "NOT_PLAYABLE_EVENT_UNAVAILABLE",
    NOT_PLAYABLE_THRESHOLD_OUT_LIMIT = "NOT_PLAYABLE_THRESHOLD_OUT_LIMIT",
    NOT_PLAYABLE_EVENT_NOT_AVAILABLE = "NOT_PLAYABLE_EVENT_NOT_AVAILABLE"
}

export interface UnPlayableReason {
    messageCode: UN_PLAYABLE_REASON_CODE;
    messageTags: Record<string, string>
}

export interface EventIsPlayable {
    isPlayable: boolean;
    reasons: UnPlayableReason[];
}

export interface Event extends Entity {
    name: string;
    referredClusterCategoryId: string;
    inheritProjectDuration: boolean;
    begin: string;
    end: string;
    periodicityStartDate: string | null;
    periodicity: EVENT_PERIODICITY_ENUM;
    reiterability: number;
    occurrences: number;
    status: EVENT_STATUS_ENUM;
    eventConfigurationStatus: EVENT_CONFIGURATION_STATUS_ENUM;
    userEventStatus: EVENT_USER_STATUS_ENUM;
    notificationEnabled: boolean;
    eventType: EventType;
    rewards: Reward[];
    products?: any;
    cmsConfig: EventCms;
    clusterEventAccesses: ClusterEventAccess[];
    isPlayable?: EventIsPlayable;
    metadata?: any;
    stats?: {
        percentage: number;
        completed: number;
        total: number;
    };
}

export interface EventType {
    logicId: string;
    category: EventCategoryEnum;
    typeName: EventTypeEnum;
    messageKey: string;
    defaultReiterability: number;
    defaultPeriodicity: string;
    defaultOverrideAllowed: boolean;
    defaultOverlapAllowed: boolean;
}

export interface EventCms {
    logicId: string;
    eventLogicId: string;
    clusterLogicId: string | null;
    title?: string;
    shortDescription?: string;
    longDescription?: string;
    baseImage?: string;
    thumbNailImage?: string;
    isVisible: boolean;
    isInteractive: boolean;
    multimediaContent: CmsSection[];
    banner: CmsSection;
    cmsBanner?: CmsBanner;
    form: FormCms;
    result: CmsSection;
    options?: EventCmsOptions;
    sections?: CmsSection[];
}

interface EventCmsOptions extends Record<any, any> {
}

interface FormCms extends Omit<CmsSection, "sections"> {
    section: CmsSection;
}

export interface ClusterEventAccess {
    logicId: string;
    referredClusterId: string;
    type: CLUSTER_EVENT_ACCESS_ENUM;
}

export interface EventFilters {
    eventGroupLogicId?: string;
    statuses?: EVENT_STATUS_ENUM[];
    ids?: string[];
    excludeIds?: string[];
    page?: number;
    size?: number;
    type?: EventTypeEnum;
}

export interface SendEventResponse {
    customData: Record<string, any> | null;
    logicId: string;
    eventConfigurationLogicId: string;
    referredUserId: string;
    key: string;
    occurrenceCount: any;
    occurrenceParentId: any;
    occurrenceTimestamp: string | null;
    status: string;
    channel: string;
    errorMessage: string | null;
    assignedBehaviours: AssignedBehaviour[];
}

export interface SendEventRequest {
    extraEventPayload?: object;
    trackEventPayload?: object;
}

export interface AssignedBehaviour {
    type: string;
    status: string;
    referredLogicId: string;
    amount: number | null;
    errorMessage: string | null;
    reward: Reward;
}

/**
 * Ext
 */
export interface VerifyFilesRequest {
    invoiceUrl: string[];
}

export interface VerifyFilesResponse {
    isInvoice: boolean;
}

/**
 * Invoice
 */
export interface InvoicesResponse {
    results: Invoice[];
}

export enum InvoiceStatusEnum {
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
    PENDING = "PENDING",
    ERROR = "ERROR",
}

export const InvoiceStatusColors: Record<InvoiceStatusEnum, string> = {
    [InvoiceStatusEnum.APPROVED]: "success",
    [InvoiceStatusEnum.REJECTED]: "error",
    [InvoiceStatusEnum.PENDING]: "neutral",
    [InvoiceStatusEnum.ERROR]: "error",
};

export interface CheckDetail {
    hasVatNumber: boolean;
    hasInvoiceDate: boolean;
    notes?: string;
    hasInvoiceNumber: boolean;
    hasItems: boolean;
    overallValid: boolean;
}

export interface Invoice {
    idInvoice: number;

    invoiceNumber?: string;
    country?: string;
    invoiceDate?: string;

    status: InvoiceStatusEnum;

    totalInvoiceAmount?: number;
    wholesaler?: string;
    wholesalerVatNumber?: string;
    wholesalerAddress?: string;

    customerVatNumber?: string;
    customerName?: string;
    customerAddress?: string;

    userId?: string;

    totalInvoiceAmountGewissProduct?: number;

    createdBy?: string;
    lastModifiedBy?: string;
    createdDate: string;
    lastModifiedDate?: string;

    eventApprovalLogicId?: string;
    eventExecutionLogicIds?: string[];

    checks?: CheckDetail;

    rejectionReason?: string;
    documentCurrency?: string;

    eventKey?: string;

    totalCoreTxAmount?: number;
    totalBoostTxAmount?: number;

    issueLogicIds?: string[];
}

